import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { LogoFull } from "./Logo";
import {
  FileText, Users, Settings, LogOut,
  CreditCard, BarChart3, Menu, X, Shield, HelpCircle, ClipboardList, RefreshCw, BookOpen, Receipt
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Invoices", icon: FileText, id: "dashboard" },
  { to: "/estimates", label: "Estimates", icon: ClipboardList, id: "estimates" },
  { to: "/recurring", label: "Recurring", icon: RefreshCw, id: "recurring" },
  { to: "/expenses", label: "Expenses", icon: Receipt, id: "expenses" },
  { to: "/clients", label: "Clients", icon: Users, id: "clients" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
  { to: "/billing", label: "Billing", icon: CreditCard, id: "billing" },
  { to: "/getting-started", label: "Getting Started", icon: HelpCircle, id: "help" },
];

const AppShell = ({ active, planInfo, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const SidebarContent = () => (
    <>
      <div className="p-5 flex items-center justify-between">
        <Link to="/" data-testid="sidebar-logo" onClick={() => setSidebarOpen(false)}>
          <LogoFull size={28} />
        </Link>
        <button
          className="lg:hidden p-1 text-[#6B7280] hover:text-[#0A0A0A]"
          onClick={() => setSidebarOpen(false)}
          data-testid="close-sidebar-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="px-3 mt-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <Link
              key={item.id}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 text-[14px] transition-colors ${
                isActive
                  ? "bg-[#F3F4F6] font-semibold text-[#0A0A0A]"
                  : "text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#0A0A0A]"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <Icon className="w-[18px] h-[18px]" />
              {item.label}
            </Link>
          );
        })}
        {user?.role === "admin" && (
          <Link
            to="/admin"
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 text-[14px] transition-colors ${
              active === "admin"
                ? "bg-[#F3F4F6] font-semibold text-[#0A0A0A]"
                : "text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#0A0A0A]"
            }`}
            data-testid="nav-admin"
          >
            <Shield className="w-[18px] h-[18px]" />
            Admin
          </Link>
        )}
        {user?.role === "admin" && (
          <Link
            to="/blog/admin"
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 text-[14px] transition-colors ${
              active === "blog"
                ? "bg-[#F3F4F6] font-semibold text-[#0A0A0A]"
                : "text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#0A0A0A]"
            }`}
            data-testid="nav-blog-admin"
          >
            <BookOpen className="w-[18px] h-[18px]" />
            Blog
          </Link>
        )}
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E5E7EB]">
        {planInfo && planInfo.plan === "free" && (
          <div className="mb-4 p-3 bg-[#F9FAFB] border border-[#E5E7EB]" data-testid="plan-usage">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-[#9CA3AF] tracking-[0.05em] uppercase">Free Plan</span>
              <span className="text-[12px] font-mono text-[#0A0A0A]">{planInfo.invoices_this_month}/{planInfo.invoice_limit}</span>
            </div>
            <div className="w-full bg-[#E5E7EB] h-1.5">
              <div
                className="h-1.5 transition-all"
                style={{
                  width: `${Math.min(100, (planInfo.invoices_this_month / planInfo.invoice_limit) * 100)}%`,
                  backgroundColor: planInfo.invoices_remaining <= 2 ? '#EF4444' : '#1A3DFF'
                }}
              />
            </div>
            {planInfo.invoices_remaining <= 3 && (
              <p className="text-[11px] text-[#EF4444] mt-1.5 font-medium">{planInfo.invoices_remaining} invoices left this month</p>
            )}
          </div>
        )}
        {planInfo && planInfo.plan === "pro" && (
          <div className="mb-4 px-3 py-1.5 bg-[#1A3DFF]/5 border border-[#1A3DFF]/20" data-testid="plan-pro-badge">
            <span className="text-[11px] font-bold text-[#1A3DFF] tracking-[0.05em] uppercase">Pro Plan</span>
          </div>
        )}
        {planInfo && planInfo.plan === "lifetime" && (
          <div className="mb-4 px-3 py-1.5 bg-[#F59E0B]/10 border border-[#F59E0B]/20" data-testid="plan-lifetime-badge">
            <span className="text-[11px] font-bold text-[#F59E0B] tracking-[0.05em] uppercase">Lifetime Plan</span>
          </div>
        )}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center font-semibold text-[13px]">
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[14px] text-[#0A0A0A] truncate">{user?.name}</p>
            <p className="text-[12px] text-[#6B7280] truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-[13px] text-[#6B7280] hover:text-[#0A0A0A] transition-colors w-full"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-[#E5E7EB] z-30 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-[#0A0A0A]"
          data-testid="open-sidebar-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        <LogoFull size={24} />
        <div className="w-9" />
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-60 bg-white border-r border-[#E5E7EB] z-50 transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="lg:ml-60 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppShell;
